import { type ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { GlassIconButton } from '../../../components/ui/glass-icon-button';

/**
 * Khung xem trước toàn màn hình — dùng chung cho trang Tệp (`FilePreviewDialog`)
 * và tài liệu dự án (`DocumentPreviewDialog`), để hai nơi xem **một** tệp trông
 * và bấm như nhau (người dùng xin 2026-10-02).
 *
 * Một dòng đầu: biểu tượng · tiêu đề · thông tin phụ, ô thanh công cụ của trình
 * xem (zoom, trang, tìm — `PreviewControls` portal vào đây), các nút chỉ-biểu-
 * tượng (`PreviewAction`), rồi nút đóng. Thân lấp phần còn lại.
 *
 * Thứ tự nút hai nơi giữ giống nhau: Sửa · Chia sẻ/Xem trong Tệp · Phiên bản ·
 * Tải về · Xoá · Đóng.
 *
 * Portal ra `body`: `position: fixed` chỉ bám khung nhìn khi không tổ tiên nào
 * có `transform` (cùng lý do với `Dialog`), và khối Tài liệu ở Tổng quan nằm
 * trong những vùng như thế.
 */
export function PreviewFrame({
  label,
  icon,
  title,
  meta,
  actions,
  toolbarSlotRef,
  onClose,
  children,
}: {
  /** Tên đọc của hộp (`aria-label`). */
  readonly label: string;
  readonly icon: ReactNode;
  readonly title: string;
  /** Thông tin phụ cạnh tiêu đề: dung lượng, loại · trạng thái · phiên bản… */
  readonly meta?: ReactNode;
  /** Các `PreviewAction`, đứng trước nút đóng. */
  readonly actions?: ReactNode;
  /** Ô cho thanh công cụ của trình xem — `null` khi thân không có trình xem. */
  readonly toolbarSlotRef?: (element: HTMLElement | null) => void;
  readonly onClose: () => void;
  readonly children: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Khoá cuộn trang phía sau + trả focus về chỗ cũ khi đóng.
  useEffect(() => {
    const restore = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      restore?.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      // Chỉ lớp trên cùng đóng: Esc trong hộp chia sẻ / xác nhận xoá mở từ đây
      // không gỡ luôn khung xem (cùng luật với `Dialog`).
      const modals = document.querySelectorAll('[aria-modal="true"]');
      if (modals.length > 0 && modals[modals.length - 1] !== boxRef.current) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      ref={boxRef}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-0 z-50 flex flex-col bg-background"
    >
      <header className="flex min-h-11 items-center gap-2 border-b border-border px-3 py-1 sm:px-4">
        {icon}
        <div className="flex min-w-0 max-w-[45%] shrink items-center gap-2 sm:max-w-md">
          <p className="min-w-0 truncate text-sm font-medium" title={title}>
            {title}
          </p>
          {meta ? (
            <div className="flex min-w-0 shrink items-center gap-2 truncate text-xs tabular-nums text-muted-foreground">
              {meta}
            </div>
          ) : null}
        </div>
        {/* Luôn có mặt và giãn ra — không có thanh công cụ thì nó đẩy nút sang phải. */}
        <div ref={toolbarSlotRef} className="flex min-w-0 flex-1 justify-center" />
        <div className="flex items-center gap-1">
          {actions}
          <GlassIconButton
            ref={closeRef}
            variant="close"
            label={t('common.close')}
            onClick={onClose}
          />
        </div>
      </header>

      <div className="relative min-h-0 flex-1 overflow-auto">{children}</div>
    </div>,
    document.body,
  );
}

/**
 * Nút chỉ-biểu-tượng của khung xem trước; tên nằm ở `aria-label` + tooltip.
 * Khoá thì `reason` thay tooltip — lý do nằm ngay chỗ người ta rê chuột.
 */
export function PreviewAction({
  label,
  icon,
  onClick,
  disabled = false,
  reason,
  pressed,
  destructive = false,
  link,
}: {
  readonly label: string;
  readonly icon: ReactNode;
  readonly onClick?: () => void;
  readonly disabled?: boolean;
  /** Tooltip khi khoá. */
  readonly reason?: string;
  readonly pressed?: boolean;
  /** Xoá: chữ đỏ khi rê chuột. */
  readonly destructive?: boolean;
  /** Nút là một liên kết (router `Link`): truyền phần tử, biểu tượng đặt bên trong nó. */
  readonly link?: ReactNode;
}): ReactNode {
  const className = destructive ? 'size-8 hover:text-destructive' : 'size-8';
  if (link && !disabled) {
    return (
      <Button
        asChild
        size="icon"
        variant="ghost"
        className={className}
        aria-label={label}
        title={label}
      >
        {link}
      </Button>
    );
  }
  return (
    <Button
      size="icon"
      variant="ghost"
      className={className}
      aria-label={label}
      title={disabled && reason ? reason : label}
      disabled={disabled}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
      onClick={onClick}
    >
      {icon}
    </Button>
  );
}
